(function (global) {
  'use strict';
  const SLUG = /^[a-z0-9][a-z0-9-]{1,98}[a-z0-9]$/;
  function config(options) {
    const url = new URL(options.apiBase);
    if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash)
      throw new Error('apiBase must be a trusted HTTPS origin/path');
    if (!SLUG.test(options.programSlug || '')) throw new Error('Invalid program slug');
    return url.href.replace(/\/$/, '') + '/affiliate-referrals/v2/' + options.programSlug;
  }
  function keys(slug) {
    return {
      bearer: `ac:v2:${slug}:bearer`,
      idem: `ac:v2:${slug}:idem`,
      receipt: `ac:v2:${slug}:receipt`,
      expiry: `ac:v2:${slug}:expiry`,
      offer: `ac:v2:${slug}:offer`,
    };
  }
  function randomSecret(cryptoImpl) {
    const bytes = new Uint8Array(32);
    cryptoImpl.getRandomValues(bytes);
    return btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }
  function randomId(cryptoImpl) {
    return cryptoImpl.randomUUID ? cryptoImpl.randomUUID() : randomSecret(cryptoImpl);
  }
  function statusLabel(status) {
    return (
      {
        active: 'Active',
        pending_verification: 'Verification needed',
        review_required: 'Staff review needed',
        disputed: 'Under review',
        rejected: 'Not approved',
        expired: 'Expired',
        revoked: 'Revoked',
      }[status] || 'Referral received'
    );
  }
  function readableDate(value) {
    if (!value) return 'After verification';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Date unavailable';
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
      date,
    );
  }
  function nextAction(receipt) {
    const messages = {
      verify_email: 'Enter the code sent to your email.',
      request_new_code: 'Request a new email code to continue verification.',
      visit_store_for_staff_verification: 'Visit the store and ask staff to verify your referral.',
      contact_store_for_review: 'Contact the store so staff can review your referral.',
    };
    return (
      messages[receipt.next_action] ||
      (receipt.status === 'active'
        ? 'Your referral is active. You can continue shopping.'
        : receipt.next_action || 'Contact the store for help with this referral.')
    );
  }
  function mount(options) {
    const root = options.root,
      fetchImpl = options.fetchImpl || global.fetch.bind(global),
      storage = options.storage || global.sessionStorage,
      cryptoImpl = options.crypto || global.crypto,
      base = config(options),
      k = keys(options.programSlug);
    if (!root || !cryptoImpl?.getRandomValues) throw new Error('Root and Web Crypto are required');
    let generation = 0,
      offer = null,
      receipt = null,
      challenge = null,
      busy = false;
    const $ = (s) => root.querySelector(s),
      forms = ['#ac-offer-form', '#ac-registration-form', '#ac-otp-form', '#ac-receipt-view'];
    function show(id) {
      forms.forEach((s) => ($(s).hidden = s !== id));
    }
    function text(sel, v) {
      $(sel).textContent = v == null ? '' : String(v);
    }
    function alert(message, kind = 'error') {
      const el = $('#ac-alert');
      el.hidden = !message;
      el.dataset.kind = kind;
      text('#ac-alert', message);
    }
    function pending(value) {
      busy = value;
      root.querySelectorAll('button').forEach((b) => (b.disabled = value));
      if (!value) $('#ac-verify').disabled = !challenge;
    }
    function clearFlow() {
      generation++;
      offer = receipt = challenge = null;
      pending(false);
      Object.values(k).forEach((x) => storage.removeItem(x));
      root.querySelectorAll('input').forEach((i) => {
        if (i.type === 'checkbox') i.checked = false;
        else i.value = '';
      });
      $('#ac-partner-card').hidden = true;
      text('#ac-offer-copy', 'Enter a referral code to view the program details.');
      text('#ac-partner-name', '');
      text('#ac-program-name', '');
      $('#ac-recovery-actions').hidden = true;
      text('#ac-status', '');
      text('#ac-receipt-heading', '');
      text('#ac-receipt-partner', '');
      text('#ac-receipt-id', '');
      text('#ac-receipt-terms', '');
      text('#ac-expires', '');
      text('#ac-next-action', '');
      text('#ac-cart-status', '');
      alert('');
      show('#ac-offer-form');
    }
    async function api(path, init = {}) {
      const response = await fetchImpl(base + path, init);
      let body = {};
      try {
        body = await response.json();
      } catch (_) {}
      if (!response.ok || body.status !== true) {
        const e = new Error(
          body.message || 'We could not complete that request. Please try again.',
        );
        e.code = body.code;
        e.status = response.status;
        throw e;
      }
      return body.data;
    }
    function authHeaders(extra = {}) {
      const bearer = storage.getItem(k.bearer);
      return Object.assign(
        { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
        extra,
      );
    }
    function renderOffer(data) {
      offer = data;
      storage.setItem(k.offer, JSON.stringify(data));
      text('#ac-offer-copy', data.policy.customer_offer_text);
      text('#ac-partner-name', data.partner.name);
      text('#ac-program-name', data.program.name);
      text('#ac-service-copy', data.policy.service_consent_text);
      text('#ac-marketing-copy', data.policy.marketing_consent_text);
      text('#ac-terms-version', `Terms ${data.policy.terms_version}`);
      $('#ac-partner-card').hidden = false;
      show('#ac-registration-form');
    }
    function renderReceipt(data) {
      receipt = data.receipt || data;
      challenge = data.challenge || null;
      const r = receipt;
      text('#ac-status', statusLabel(r.status));
      text('#ac-receipt-partner', r.partner_name);
      text('#ac-receipt-id', r.receipt_id);
      text('#ac-receipt-terms', r.terms_version);
      text('#ac-expires', readableDate(r.expires_at));
      text('#ac-next-action', nextAction(r));
      text('#ac-receipt-heading', r.status === 'active' ? 'Referral active' : 'Referral received');
      storage.setItem(k.receipt, r.receipt_id);
      if (data.flow_expires_at) storage.setItem(k.expiry, data.flow_expires_at);
      show(
        r.status === 'pending_verification' && data.verification_method === 'email_otp'
          ? '#ac-otp-form'
          : '#ac-receipt-view',
      );
      if (r.status === 'pending_verification' && data.verification_method === 'email_otp') {
        text('#ac-otp-instructions', nextAction(r));
      }
      $('#ac-verify').disabled = !challenge;
      if (r.status === 'active') persistCart(r);
    }
    async function loadOffer(code) {
      if (busy) return;
      const mine = ++generation;
      pending(true);
      alert('');
      try {
        const data = await api('/offer?code=' + encodeURIComponent(code));
        if (mine === generation) renderOffer(data);
      } catch (e) {
        if (mine === generation) alert(e.message);
      } finally {
        if (mine === generation) pending(false);
      }
    }
    async function register() {
      if (busy || !offer) return;
      const mine = ++generation;
      let bearer = storage.getItem(k.bearer),
        idem = storage.getItem(k.idem);
      if (!bearer) {
        bearer = randomSecret(cryptoImpl);
        idem = randomId(cryptoImpl);
        storage.setItem(k.bearer, bearer);
        storage.setItem(k.idem, idem);
      }
      const payload = {
        code: offer.partner.code,
        name: $('#ac-name').value.trim(),
        email: $('#ac-email').value.trim() || null,
        phone: $('#ac-phone').value.trim() || null,
        service_consent: $('#ac-service-consent').checked,
        marketing_consent: $('#ac-marketing-consent').checked,
        terms_version: offer.policy.terms_version,
        offer_fingerprint: offer.policy.offer_fingerprint,
      };
      pending(true);
      alert('');
      try {
        const data = await api('/registrations', {
          method: 'POST',
          headers: authHeaders({ 'Idempotency-Key': idem }),
          body: JSON.stringify(payload),
        });
        if (mine === generation) renderReceipt(data);
      } catch (e) {
        if (mine === generation) alert(e.message);
      } finally {
        if (mine === generation) pending(false);
      }
    }
    async function verify() {
      if (busy || !receipt || !challenge) return;
      const mine = ++generation;
      pending(true);
      try {
        const data = await api(`/registrations/${encodeURIComponent(receipt.receipt_id)}/verify`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ challenge_id: challenge.id, otp: $('#ac-otp').value.trim() }),
        });
        if (mine === generation) {
          renderReceipt(data);
          show('#ac-receipt-view');
          alert(nextAction(data.receipt), data.receipt.status === 'active' ? 'success' : 'info');
        }
      } catch (e) {
        if (mine === generation) alert(e.message);
      } finally {
        if (mine === generation) pending(false);
      }
    }
    async function resend() {
      if (busy || !receipt || receipt.status !== 'pending_verification') return;
      const mine = ++generation;
      pending(true);
      try {
        const data = await api(`/registrations/${encodeURIComponent(receipt.receipt_id)}/resend`, {
          method: 'POST',
          headers: authHeaders(),
          body: '{}',
        });
        if (mine === generation) {
          renderReceipt(data);
          alert(nextAction(data.receipt), 'success');
        }
      } catch (e) {
        if (mine === generation) alert(e.message);
      } finally {
        if (mine === generation) pending(false);
      }
    }
    async function recover() {
      if (busy) return true;
      const id = storage.getItem(k.receipt),
        bearer = storage.getItem(k.bearer),
        expiry = storage.getItem(k.expiry),
        savedOffer = storage.getItem(k.offer);
      if (!id) {
        if (bearer && storage.getItem(k.idem) && savedOffer) {
          try {
            renderOffer(JSON.parse(savedOffer));
            alert('Re-enter the same contact details to retry the original request.', 'info');
            return true;
          } catch (_) {}
        }
        return false;
      }
      if (
        !bearer ||
        !expiry ||
        !Number.isFinite(Date.parse(expiry)) ||
        Date.parse(expiry) <= Date.now()
      ) {
        clearFlow();
        return false;
      }
      const mine = ++generation;
      pending(true);
      try {
        const data = await api(`/registrations/${encodeURIComponent(id)}`, {
          headers: authHeaders(),
        });
        if (mine === generation) {
          renderReceipt(data);
          $('#ac-recovery-actions').hidden = true;
        }
      } catch (e) {
        if (mine === generation) {
          alert(`${e.message} Use Start over only if you want to abandon this recovery.`, 'error');
          $('#ac-recovery-actions').hidden = false;
        }
      } finally {
        if (mine === generation) pending(false);
      }
      return true;
    }
    async function persistCart(r) {
      if (!options.cartPersistence?.enabled) return;
      const endpoint = options.cartPersistence.endpoint;
      if (endpoint !== '/cart/update.js') {
        text('#ac-cart-status', 'Referral verified. Cart linking is unavailable.');
        return;
      }
      const mine = generation;
      try {
        const response = await fetchImpl(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              affiliate_claim_ref: r.receipt_id,
              affiliate_claim_version: String(r.policy_version),
            },
          }),
        });
        if (!response.ok) throw new Error();
        if (mine !== generation) return;
        text('#ac-cart-status', 'Referral saved to this cart.');
      } catch (_) {
        if (mine !== generation || !receipt || r.receipt_id !== receipt.receipt_id) return;
        text(
          '#ac-cart-status',
          'Referral verified, but this cart could not be linked. You can continue shopping or try again later.',
        );
      }
    }
    $('#ac-offer-form').addEventListener('submit', (e) => {
      e.preventDefault();
      loadOffer($('#ac-code').value.trim());
    });
    $('#ac-registration-form').addEventListener('submit', (e) => {
      e.preventDefault();
      if (!$('#ac-email').value.trim() && !$('#ac-phone').value.trim())
        return alert('Enter an email or phone number.');
      if ($('#ac-phone').value.trim() && !/^\+[1-9]\d{7,14}$/.test($('#ac-phone').value.trim()))
        return alert('Enter phone as + followed by country code and digits only.');
      register();
    });
    $('#ac-otp-form').addEventListener('submit', (e) => {
      e.preventDefault();
      verify();
    });
    $('#ac-resend').addEventListener('click', resend);
    $('#ac-back').addEventListener('click', clearFlow);
    $('#ac-code').addEventListener('change', () => {
      if (!offer && !receipt && !challenge) return;
      const code = $('#ac-code').value.trim();
      clearFlow();
      $('#ac-code').value = code;
    });
    $('#ac-start-over').addEventListener('click', clearFlow);
    $('#ac-retry-recovery').addEventListener('click', recover);
    $('#ac-recovery-start-over').addEventListener('click', clearFlow);
    const ready = (async () => {
      const recovery = recover();
      const initialGeneration = generation;
      if ((await recovery) || generation !== initialGeneration) return;
      if (options.initialCode) {
        $('#ac-code').value = options.initialCode;
        await loadOffer(options.initialCode);
      }
    })();
    return {
      ready,
      loadOffer,
      register,
      verify,
      resend,
      recover,
      reset: clearFlow,
      getState: () => ({ offer, receipt, challenge, busy, generation }),
    };
  }
  global.AffiliateCaptureV2 = { mount, storageKeys: keys, randomSecret };
})(window);
